import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppHeader from "@/components/AppHeader";
import FileSidebar from "@/components/FileSidebar";
import GridEditor from "@/components/GridEditor";
import { WORKSPACE } from "@/constants/testIds";
import { FileSpreadsheet, ArrowLeft, Maximize2, Minimize2, ChevronLeft, ChevronRight, LayoutList, Menu, X } from "lucide-react";

// Match Tailwind's `md` breakpoint (768px)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export default function Workspace() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFileId, setActiveFileId] = useState(null);
  const [focus, setFocus] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // On mobile the sidebar is a slide-in drawer; on desktop it's inline.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Auto-close the mobile drawer once a file is selected so the user sees the
  // PDF preview immediately.
  useEffect(() => {
    if (isMobile && activeFileId) setMobileSidebarOpen(false);
  }, [activeFileId, isMobile]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/files");
      setFiles(data.files || []);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll lock status every 15s so sidebar badges stay fresh
  useEffect(() => {
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);

  const activeIndex = useMemo(
    () => (activeFileId ? files.findIndex((f) => f.id === activeFileId) : -1),
    [files, activeFileId]
  );
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex >= 0 && activeIndex < files.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrev) setActiveFileId(files[activeIndex - 1].id);
  }, [hasPrev, files, activeIndex]);
  const goNext = useCallback(() => {
    if (hasNext) setActiveFileId(files[activeIndex + 1].id);
  }, [hasNext, files, activeIndex]);

  // ---- Global keyboard shortcuts for fast file navigation ----
  useEffect(() => {
    const handler = (e) => {
      // Ignore typing in inputs/textareas/contenteditable and edit mode grid
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      } else if (e.key === "f" || e.key === "F") {
        // 'F' toggles focus (Excel-like presentation mode)
        setFocus((v) => !v);
      } else if (e.key === "Escape" && focus) {
        setFocus(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goPrev, goNext, focus]);

  // ---- Prefetch PDFs so clicks feel instant ----
  const prefetchedIds = useRef(new Set());
  // Prime the browser HTTP cache for a set of file IDs, one at a time so we
  // don't hammer the backend / user's bandwidth.
  const prefetch = useCallback(async (ids) => {
    for (const id of ids) {
      if (!id || prefetchedIds.current.has(id)) continue;
      prefetchedIds.current.add(id);
      try {
        // `cache: "default"` (not "no-store") lets the browser HTTP cache the
        // response so the eventual click reads from local cache.
        await fetch(`${API_BASE}/files/${id}/preview.pdf`, {
          credentials: "include",
        });
      } catch {
        prefetchedIds.current.delete(id);
      }
    }
  }, []);

  // As soon as the file list loads, prefetch the top 5 files so first clicks
  // are instant.
  useEffect(() => {
    if (!files.length) return;
    prefetch(files.slice(0, 5).map((f) => f.id));
  }, [files, prefetch]);

  // When a file is opened, prefetch its ±2 neighbours for smooth prev/next.
  useEffect(() => {
    if (!activeFileId) return;
    const neighbours = [];
    if (hasPrev) neighbours.push(files[activeIndex - 1]?.id);
    if (hasNext) neighbours.push(files[activeIndex + 1]?.id);
    if (activeIndex - 2 >= 0) neighbours.push(files[activeIndex - 2]?.id);
    if (activeIndex + 2 < files.length) neighbours.push(files[activeIndex + 2]?.id);
    prefetch(neighbours);
  }, [activeFileId, files, activeIndex, hasPrev, hasNext, prefetch]);

  const activeFile = activeIndex >= 0 ? files[activeIndex] : null;

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      {!focus && <AppHeader onOpenSidebar={isMobile ? () => setMobileSidebarOpen(true) : undefined} />}
      <div className="flex-1 flex min-h-0 relative">
        {!focus && !isMobile && (
          <FileSidebar
            files={files}
            loading={loading}
            activeFileId={activeFileId}
            onSelect={setActiveFileId}
            onRefresh={load}
            currentUserId={user?.id}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        )}
        {!focus && isMobile && mobileSidebarOpen && (
          <>
            <div
              data-testid="mobile-sidebar-backdrop"
              className="fixed inset-0 z-40 bg-black/50"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <div className="fixed top-0 left-0 bottom-0 w-[85vw] max-w-sm z-50 bg-background shadow-2xl flex flex-col animate-in slide-in-from-left duration-200">
              <div className="h-12 flex-shrink-0 flex items-center justify-between px-3 border-b border-border/60">
                <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{t("files")}</span>
                <button
                  data-testid="mobile-sidebar-close"
                  onClick={() => setMobileSidebarOpen(false)}
                  className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-accent"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <FileSidebar
                  files={files}
                  loading={loading}
                  activeFileId={activeFileId}
                  onSelect={setActiveFileId}
                  onRefresh={load}
                  currentUserId={user?.id}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                />
              </div>
            </div>
          </>
        )}
        <main className="flex-1 flex flex-col min-w-0 min-h-0 relative">
          {focus && (
            <FocusBar
              file={activeFile}
              index={activeIndex}
              total={files.length}
              hasPrev={hasPrev}
              hasNext={hasNext}
              onPrev={goPrev}
              onNext={goNext}
              onExit={() => setFocus(false)}
            />
          )}
          {activeFileId ? (
            <GridEditor
              key={activeFileId}
              fileId={activeFileId}
              currentUser={user}
              onLockChange={load}
              focus={focus}
              onToggleFocus={() => setFocus((v) => !v)}
              hasPrev={hasPrev}
              hasNext={hasNext}
              onPrev={goPrev}
              onNext={goNext}
              onRefresh={load}
            />
          ) : (
            <EmptyState onOpenSidebar={isMobile ? () => setMobileSidebarOpen(true) : undefined} />
          )}
        </main>
      </div>
    </div>
  );
}

function FocusBar({ file, index, total, hasPrev, hasNext, onPrev, onNext, onExit }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="focus-toolbar"
      className="h-12 flex-shrink-0 flex items-center justify-between px-4 border-b border-border/60 bg-background/95 backdrop-blur-xl z-30"
    >
      <div className="flex items-center gap-2">
        <button
          data-testid="focus-exit-btn"
          onClick={onExit}
          className="btn-swiss h-9 px-3 gap-1.5"
          title={`${t("exit_focus")} (Esc)`}
        >
          <LayoutList className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-xs">{t("exit_focus")}</span>
        </button>
      </div>

      <div className="flex items-center gap-2 min-w-0 px-3">
        <FileSpreadsheet className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
        <div className="font-display font-bold text-sm tracking-tight truncate">
          {file?.name || "—"}
        </div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground pl-2 flex-shrink-0">
          {index >= 0 ? `${index + 1} / ${total}` : ""}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          data-testid="focus-prev-btn"
          onClick={onPrev}
          disabled={!hasPrev}
          className="btn-swiss h-9 px-3 gap-1"
          title={`${t("prev_file")} (←)`}
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-xs">{t("prev_file")}</span>
        </button>
        <button
          data-testid="focus-next-btn"
          onClick={onNext}
          disabled={!hasNext}
          className="btn-swiss primary h-9 px-3 gap-1"
          title={`${t("next_file")} (→)`}
        >
          <span className="text-xs">{t("next_file")}</span>
          <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onOpenSidebar }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={WORKSPACE.emptyState}
      className="flex-1 flex items-center justify-center p-10 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, hsl(var(--border)/0.6) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)/0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative text-center max-w-md">
        <div className="mx-auto w-16 h-16 rounded-lg border-2 border-[hsl(var(--brand))] bg-white flex items-center justify-center mb-6 shadow-[0_8px_24px_-8px_hsl(var(--brand)/0.35)]">
          <FileSpreadsheet className="w-8 h-8 text-[hsl(var(--brand))]" strokeWidth={1.5} />
        </div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
          // 00 / WORKSPACE
        </div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-3">
          {t("empty_no_selection")}
        </h2>
        <p className="text-sm text-muted-foreground">{t("empty_no_selection_hint")}</p>
        {onOpenSidebar ? (
          <button
            data-testid="empty-open-sidebar-btn"
            onClick={onOpenSidebar}
            className="btn-swiss primary mt-8 h-10 px-4 gap-2"
          >
            <LayoutList className="w-4 h-4" strokeWidth={1.75} />
            <span className="text-xs uppercase tracking-widest">{t("files")}</span>
          </button>
        ) : (
          <div className="mt-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            {t("files")}
          </div>
        )}
      </div>
    </div>
  );
}
